import React, { useEffect, useMemo, useState } from 'react';
import { Building2, FolderKanban, Layers, Search, UserRound } from 'lucide-react';
import { apiService } from '../../services/api';

// À quoi rattacher une réunion : un projet, un client ou un programme (plusieurs projets : Nexus dit ensuite ce qui
// revient à chacun), ou personne. La recherche ignore accents et majuscules et regarde le numéro, le nom, le client et
// le programme d'un projet (« safran », « olista 70 », « 118 »).

let cached = null;

export const useScopeOptions = () => {
  const [options, setOptions] = useState(cached);
  useEffect(() => {
    let alive = true;
    apiService.get('/meetings/scopes')
      .then((data) => { cached = data; if (alive) setOptions(data); })
      .catch(() => { if (alive) setOptions((current) => current || { projects: [], clients: [] }); });
    return () => { alive = false; };
  }, []);
  return options;
};

export const SCOPE_KINDS = {
  project: { label: 'Projet', icon: FolderKanban, tone: 'text-slate-500', chip: 'bg-slate-100 text-slate-700' },
  client: { label: 'Client', icon: Building2, tone: 'text-blue-600', chip: 'bg-blue-50 text-blue-700' },
  programme: { label: 'Programme', icon: Layers, tone: 'text-amber-600', chip: 'bg-amber-50 text-amber-800' },
  none: { label: 'Sans projet', icon: UserRound, tone: 'text-violet-600', chip: 'bg-violet-50 text-violet-700' },
};

export const scopeKey = (scope) => (scope ? `${scope.kind}:${scope.id || 0}` : '');

// « Client · Safran Aircraft Engines », « OLISTA 70 », « Sans projet ».
export const scopeLabel = (scope) => {
  if (!scope || scope.kind === 'none') return 'Sans projet';
  return scope.kind === 'project' ? scope.name : `${SCOPE_KINDS[scope.kind].label} · ${scope.name}`;
};

// Champs de l'API d'une réunion pour cette portée.
export const scopeFields = (scope) => ({
  project_id: scope?.kind === 'project' ? scope.id : null,
  client_id: scope?.kind === 'client' ? scope.id : null,
  programme_id: scope?.kind === 'programme' ? scope.id : null,
});

const plain = (text) => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

// Réunion d'un client ou d'un programme (listes) : sa portée, et le nombre de projets que Nexus y a trouvés.
export const ScopeChip = ({ meeting }) => {
  const meta = SCOPE_KINDS[meeting.scope.kind];
  const Icon = meta.icon;
  const names = (meeting.projects || []).map((p) => p.name);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${meta.chip}`}
      title={names.length ? `Projets concernés : ${names.join(', ')}` : undefined}>
      <Icon className="h-3 w-3" /> {scopeLabel(meeting.scope)}
      {names.length > 0 && <span className="opacity-70">· {plural(names.length, 'projet')}</span>}
    </span>
  );
};

export const searchScopes = (options, query, { kinds = ['project', 'client', 'programme'], exclude = [] } = {}) => {
  const words = plain(query).split(/\s+/).filter(Boolean);
  const skip = new Set(exclude);
  const hit = (...fields) => {
    const text = plain(fields.filter(Boolean).join(' '));
    return words.every((word) => text.includes(word));
  };
  const projects = options?.projects || [];
  const projectNames = {};
  projects.forEach((p) => {
    if (p.programme_id) projectNames[p.programme_id] = `${projectNames[p.programme_id] || ''} ${p.name}`;
  });
  return {
    projects: !kinds.includes('project') ? [] : projects
      .filter((p) => !skip.has(`project:${p.id}`))
      .filter((p) => (words.length ? hit(p.project_number, p.name, p.client_name, p.programme_name)
        : p.status !== 'completed'))
      .slice(0, words.length ? 8 : 6),
    clients: !kinds.includes('client') ? [] : (options?.clients || [])
      .filter((c) => !skip.has(`client:${c.id}`))
      .filter((c) => (words.length ? hit(c.name) : c.active_projects > 1))
      .slice(0, 4),
    programmes: !kinds.includes('programme') ? [] : (options?.programmes || [])
      .filter((g) => !skip.has(`programme:${g.id}`))
      .filter((g) => (words.length ? hit(g.name, projectNames[g.id]) : g.active_projects > 1))
      .slice(0, 4),
  };
};

const Row = ({ kind, title, detail, muted, onClick }) => {
  const meta = SCOPE_KINDS[kind];
  const Icon = meta.icon;
  return (
    <button type="button" onClick={onClick}
      className="flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-slate-50">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.tone}`} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate ${muted ? 'text-slate-400' : 'text-slate-900'}`}>{title}</span>
        {detail && <span className="block truncate text-xs text-slate-500">{detail}</span>}
      </span>
    </button>
  );
};

const Group = ({ title, children }) => (
  <div className="py-1">
    <p className="px-2.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{title}</p>
    {children}
  </div>
);

const ScopePicker = ({ onPick, kinds, exclude = [], suggestions = [], allowNone = false, autoFocus = true,
  placeholder }) => {
  const options = useScopeOptions();
  const [query, setQuery] = useState('');
  const allowed = kinds || ['project', 'client', ...(options?.programmes ? ['programme'] : [])];
  const found = useMemo(() => searchScopes(options, query, { kinds: allowed, exclude }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, query, allowed.join(), exclude.join()]);
  const proposed = query ? [] : suggestions.filter((s) => !exclude.includes(scopeKey(s)) && (allowed.includes(s.kind)
    || (s.kind === 'none' && allowNone)));
  const shown = new Set(proposed.map(scopeKey));
  const pick = (scope) => { setQuery(''); onPick(scope); };
  const empty = !proposed.length && !found.projects.length && !found.clients.length && !found.programmes.length;
  const what = allowed.length === 1 ? 'un projet' : allowed.includes('programme')
    ? 'un projet, un client ou un programme' : 'un projet ou un client';

  return (
    <div>
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 focus-within:border-blue-300">
        <Search className="h-4 w-4 text-slate-400" />
        <input autoFocus={autoFocus} value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || `Chercher ${what}…`} className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
      </div>
      <div className="mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-100 p-1">
        {!options && <p className="px-2.5 py-2 text-sm text-slate-400">Chargement…</p>}
        {proposed.length > 0 && (
          <Group title="Proposés">
            {proposed.map((s) => (
              <Row key={scopeKey(s)} kind={s.kind} title={s.kind === 'none' ? 'Sans projet' : s.name} detail={s.reason}
                onClick={() => pick(s)} />
            ))}
          </Group>
        )}
        {found.programmes.filter((g) => !shown.has(`programme:${g.id}`)).length > 0 && (
          <Group title="Programmes · Nexus répartit entre ses projets">
            {found.programmes.filter((g) => !shown.has(`programme:${g.id}`)).map((g) => (
              <Row key={`g${g.id}`} kind="programme" title={g.name} detail={plural(g.active_projects, 'projet') + ' en cours ou proposés'}
                onClick={() => pick({ kind: 'programme', id: g.id, name: g.name })} />
            ))}
          </Group>
        )}
        {found.clients.filter((c) => !shown.has(`client:${c.id}`)).length > 0 && (
          <Group title="Clients · Nexus répartit entre leurs projets">
            {found.clients.filter((c) => !shown.has(`client:${c.id}`)).map((c) => (
              <Row key={`c${c.id}`} kind="client" title={c.name}
                detail={c.active_projects ? `${plural(c.active_projects, 'projet')} en cours ou proposés` : 'aucun projet en cours'}
                onClick={() => pick({ kind: 'client', id: c.id, name: c.name })} />
            ))}
          </Group>
        )}
        {found.projects.filter((p) => !shown.has(`project:${p.id}`)).length > 0 && (
          <Group title="Projets">
            {found.projects.filter((p) => !shown.has(`project:${p.id}`)).map((p) => (
              <Row key={`p${p.id}`} kind="project" muted={p.status === 'completed'}
                title={`${p.project_number && !['x', '*', '---'].includes(p.project_number) ? `${p.project_number} · ` : ''}${p.name}`}
                detail={[p.client_name, p.programme_name && `programme ${p.programme_name}`,
                  p.status === 'proposition' && 'proposition', p.status === 'completed' && 'terminé'].filter(Boolean).join(' · ')}
                onClick={() => pick({ kind: 'project', id: p.id, name: p.name })} />
            ))}
          </Group>
        )}
        {options && empty && <p className="px-2.5 py-2 text-sm text-slate-400">Rien ne correspond.</p>}
        {allowNone && !shown.has('none:0') && (
          <button type="button" onClick={() => pick({ kind: 'none', id: null, name: 'Sans projet' })}
            className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-2.5 py-2 text-left text-sm text-violet-700 hover:bg-violet-50">
            <UserRound className="h-4 w-4" /> Sans projet : réunion liée aux personnes
          </button>
        )}
      </div>
    </div>
  );
};

export default ScopePicker;
